/**
 * Sawyer E4 (#754) — Team messages: the staff board and person-to-person
 * messages, for every employee with a login.
 *
 * Sawyer, HTHD floor walk 2026-10-23: "a message form for the employees. So
 * we can all talk to each other... like a general message board and then like
 * person to person messaging." Named "Team messages", not "Messages": the desk
 * roles also see "Conversations" (customer texts), and two nav items both
 * meaning "messages" would fail the Counter Test.
 *
 * Routes: /team-messages (Board), /team-messages/direct (thread list),
 * /team-messages/direct/:threadId (one thread). The tab counts come from the
 * same shared unread poll as the nav badge (P1: one source).
 *
 * Principles served (docs/DESIGN-PHILOSOPHY.md): P1 one source for every
 * count; P2 one filled primary per view (Post / New message / Send); P3 the
 * dog's name is the chip; P4 optimistic post and send, skeleton rows; P5 a
 * failed load is an error, a failed refresh is a stale line, never an empty
 * board; P6 tokens only; P7 44px targets, composer at the thumb on phones.
 */
import { NavLink, useLocation } from 'react-router-dom';
import type { ClientConfig } from '@ferro/config';
import { PageHeader } from '@ferro/ui';
import { useAuth } from '../contexts/AuthContext.js';
import { BoardPanel } from '../components/team-messages/BoardPanel.js';
import { DirectPanel } from '../components/team-messages/DirectPanel.js';
import { useStaffMessagesUnread } from '../components/team-messages/useStaffMessagesUnread.js';
import { useTeamMessagesAccess } from '../components/team-messages/useTeamMessagesAccess.js';
import './team-messages.css';

type TeamMessagesProps = { config: ClientConfig };

/** `/team-messages/direct/:threadId` → threadId; anything else → null. */
export function threadIdFromPath(pathname: string): string | null {
  const m = /^\/team-messages\/direct\/([^/]+)\/?$/.exec(pathname);
  return m ? decodeURIComponent(m[1]!) : null;
}

function tabLabel(name: string, n: number | undefined): string {
  return n && n > 0 ? `${name} (${n > 99 ? '99+' : n})` : name;
}

export function TeamMessages({ config }: TeamMessagesProps): JSX.Element {
  const apiUrl = config.deployment.apiUrl;
  const { pathname } = useLocation();
  const { staff } = useAuth();
  const access = useTeamMessagesAccess();
  const unread = useStaffMessagesUnread(apiUrl, access.visible);
  const direct = pathname.startsWith('/team-messages/direct');
  const threadId = threadIdFromPath(pathname);
  const me = {
    name: `${staff?.first_name ?? ''} ${staff?.last_name ?? ''}`.trim(),
    role: staff?.role ?? '',
  };

  return (
    <section className={`ferro-admin-page ferro-tm${threadId ? ' ferro-tm--thread-open' : ''}`} data-testid="team-messages-page">
      <PageHeader title="Team messages" className="ferro-tm__header" />
      <nav className="ferro-tm-tabs" aria-label="Team messages">
        <NavLink
          to="/team-messages"
          end
          className={({ isActive }) => `ferro-tm-tab${isActive ? ' ferro-tm-tab--active' : ''}`}
          data-testid="team-messages-tab-board"
        >
          {tabLabel('Board', unread?.board)}
        </NavLink>
        <NavLink
          to="/team-messages/direct"
          className={() => `ferro-tm-tab${direct ? ' ferro-tm-tab--active' : ''}`}
          aria-current={direct ? 'page' : undefined}
          data-testid="team-messages-tab-direct"
        >
          {tabLabel('Direct', unread?.direct)}
        </NavLink>
      </nav>
      {direct ? (
        <DirectPanel apiUrl={apiUrl} threadId={threadId} canWrite={access.canWrite} />
      ) : (
        <BoardPanel apiUrl={apiUrl} canWrite={access.canWrite} canOpenDogs={access.canOpenDogs} me={me} />
      )}
    </section>
  );
}
